import { Link } from "react-router-dom";
import { Sun, ShieldCheck, Users, MapPin, ArrowRight } from "lucide-react";
import { Seo } from "../components/Seo";
import { CtaBand } from "../components/CtaBand";

export default function About() {
  return (
    <div data-testid="about-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-8">
      <Seo
        title="About 1st Nations Security | Indigenous-Owned Ontario Security"
        description="1st Nations Security is a 100% Indigenous-owned security guard company with Northwestern Ontario roots, providing Ontario security services across the province."
        path="/about-1st-nations-security"
      />
      <p className="text-xs font-semibold uppercase tracking-widest text-amber-500 mb-3">About Us</p>
      <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-white font-heading mb-6">
        1st Nations Security — Ontario Security Services
      </h1>
      <div className="max-w-3xl space-y-6">
        <p className="text-base text-slate-300 leading-relaxed">
          1st Nations Security is a 100% Indigenous-owned security guard company serving all of Ontario. Founded with
          deep roots in Northwestern Ontario — the 807 region of Thunder Bay, Kenora, Dryden, Sioux Lookout and the
          surrounding First Nations — we built this company to raise the standard of professional security services
          in Ontario while creating real opportunity for Indigenous people in the security industry.
        </p>
        <p className="text-base text-slate-300 leading-relaxed">
          The rising sun in our logo stands for vigilance, warmth and a new day. Those values guide how we protect
          our clients: uniformed security guards who are visible and professional, mobile patrol teams that show up
          when they say they will, and a 24/7 dispatch centre that always answers the phone at +1 807-770-0243.
        </p>
        <p className="text-base text-slate-300 leading-relaxed">
          Today we provide Ontario security services to businesses, institutions and communities in more than 45
          cities — construction site security in the GTA, healthcare security in regional hospitals, mining and
          industrial security in the north, event security and crowd management province-wide, and culturally
          respectful Indigenous community security for First Nations across Ontario.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 my-14">
        {[
          { icon: Sun, h: "100% Indigenous-Owned", p: "A First Nations security company reinvesting in the communities we protect." },
          { icon: ShieldCheck, h: "Licensed & Professional", p: "Ontario-licensed security guards trained in de-escalation, first aid and report writing." },
          { icon: Users, h: "Community-First Hiring", p: "We recruit and train local guards — including First Nations community members — wherever we work." },
          { icon: MapPin, h: "All of Ontario", p: "From Northwestern Ontario to the GTA, Ottawa and Windsor — one call covers the province." },
        ].map((v) => (
          <div key={v.h} data-testid={`about-value-${v.h.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="rounded-2xl border border-white/10 bg-slate-900/60 p-6">
            <v.icon className="h-8 w-8 text-amber-400 mb-4" />
            <h2 className="text-lg font-semibold text-white mb-2">{v.h}</h2>
            <p className="text-sm text-slate-400 leading-relaxed">{v.p}</p>
          </div>
        ))}
      </div>

      <section data-testid="about-commitment-section" className="rounded-2xl border border-amber-500/20 bg-slate-900/60 p-8 max-w-3xl">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white font-heading mb-4">Our Commitment to Ontario</h2>
        <p className="text-base text-slate-300 leading-relaxed mb-4">
          Every contract we win creates jobs, training and pride in Ontario communities — urban, rural, northern and
          First Nations alike. When you hire 1st Nations Security, you get professional Ontario security services and
          you support Indigenous economic development at the same time.
        </p>
        <Link to="/security-services/indigenous-community-security" data-testid="about-indigenous-security-link" className="inline-flex items-center gap-2 text-sm font-semibold text-amber-400 hover:text-amber-300">
          Indigenous community security services <ArrowRight className="h-4 w-4" />
        </Link>
      </section>

      <p className="text-sm text-slate-500 mb-4 max-w-3xl">
        Part of the 1st Nations Security family — also visit{" "}
        <a href="https://1stnation-security.com" data-testid="about-sister-site-link" rel="noopener" className="text-amber-400 hover:text-amber-300">
          1stnation-security.com
        </a>
        .
      </p>

      <CtaBand
        heading="Work With a First Nations Security Company"
        sub="Call +1 807-770-0243 or email admin@1stnation-security.com to discuss security guard services for your Ontario site, facility, event or community."
      />
    </div>
  );
}
